import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { ROAD_CONDITIONS } from './roadsImporter.constants';
import { sanitizeIntegerInput } from './roadsImporter.helpers';

export interface SaveRoadFormState {
  roadName: string;
  returnRoadName: string;
  sameNameBothWays: boolean;
  minutes: string;
  capacity: string;
  weightCapacity: string;
  speedLimitMin: string;
  speedLimitMax: string;
  lanes: string;
  condition: string;
  suggestions: string;
  twoWay: boolean;
}

interface SaveRoadDialogProps {
  open: boolean;
  fromName: string;
  toName: string;
  distanceLabel: string;
  value: SaveRoadFormState;
  onChange: (next: SaveRoadFormState) => void;
  onClose: () => void;
  onSave: () => void;
}

// Shown once the drawn road reaches its destination location — collects
// the road's details before it's added to the pending-routes queue.
export function SaveRoadDialog({
  open,
  fromName,
  toName,
  distanceLabel,
  value,
  onChange,
  onClose,
  onSave,
}: SaveRoadDialogProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('roads_importer_road_dialog_title', { from: fromName, to: toName })}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 1 }}
      >
        <FormControlLabel
          sx={{ gridColumn: '1 / -1', mt: 1 }}
          control={
            <Checkbox
              checked={value.twoWay}
              onChange={(e) => {
                onChange({ ...value, twoWay: e.target.checked });
              }}
            />
          }
          label={t('roads_importer_field_two_way')}
        />
        <TextField
          autoFocus
          size="small"
          label={
            value.twoWay
              ? t('roads_importer_field_road_name_forward')
              : t('roads_importer_field_road_name')
          }
          value={value.roadName}
          onChange={(e) => {
            onChange({ ...value, roadName: e.target.value });
          }}
          sx={{ gridColumn: value.twoWay ? undefined : '1 / -1' }}
        />
        {value.twoWay && (
          <TextField
            size="small"
            label={t('roads_importer_field_road_name_return')}
            value={
              value.sameNameBothWays ? value.roadName : value.returnRoadName
            }
            disabled={value.sameNameBothWays}
            onChange={(e) => {
              onChange({ ...value, returnRoadName: e.target.value });
            }}
          />
        )}
        {value.twoWay && (
          <FormControlLabel
            sx={{ gridColumn: '1 / -1', mt: -1 }}
            control={
              <Checkbox
                size="small"
                checked={value.sameNameBothWays}
                onChange={(e) => {
                  onChange({ ...value, sameNameBothWays: e.target.checked });
                }}
              />
            }
            label={t('roads_importer_field_same_road_name_both_ways')}
          />
        )}
        <TextField
          size="small"
          label={t('roads_importer_field_distance')}
          value={distanceLabel}
          InputProps={{ readOnly: true }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_minutes')}
          value={value.minutes}
          onChange={(e) => {
            onChange({
              ...value,
              minutes: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_capacity')}
          value={value.capacity}
          onChange={(e) => {
            onChange({
              ...value,
              capacity: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_weight_capacity')}
          value={value.weightCapacity}
          onChange={(e) => {
            onChange({
              ...value,
              weightCapacity: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_speed_limit_min')}
          value={value.speedLimitMin}
          onChange={(e) => {
            onChange({
              ...value,
              speedLimitMin: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_speed_limit_max')}
          value={value.speedLimitMax}
          onChange={(e) => {
            onChange({
              ...value,
              speedLimitMax: sanitizeIntegerInput(e.target.value),
            });
          }}
        />
        <TextField
          size="small"
          type="number"
          label={t('roads_importer_field_lanes')}
          value={value.lanes}
          onChange={(e) => {
            onChange({ ...value, lanes: sanitizeIntegerInput(e.target.value) });
          }}
        />
        <TextField
          select
          size="small"
          label={t('roads_importer_field_road_condition')}
          value={value.condition}
          onChange={(e) => {
            onChange({ ...value, condition: e.target.value });
          }}
        >
          {ROAD_CONDITIONS.map((condition) => (
            <MenuItem key={condition} value={condition}>
              {t(`roads_importer_condition_${condition}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('roads_importer_field_geo_suggestions')}
          value={value.suggestions}
          onChange={(e) => {
            onChange({ ...value, suggestions: e.target.value });
          }}
          sx={{ gridColumn: '1 / -1' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckIcon />}
          onClick={onSave}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('roads_importer_btn_add_to_queue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
